import { useMutation, useQuery } from '@apollo/react-hooks';
import { UAContext } from '@quentin-sommer/react-useragent';
import cn from 'classnames';
import { Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import * as Yup from 'yup';
import Button from '~components/button';
import { NumberInput, SelectMulti } from '~components/form';
import { Card } from '~components/navigation-panel/section-card';
import { useToast } from '~components/toast/use-toast';
import { Authorization } from '~constants/cookie-properties';
import {
  CountAvailableBedNumber,
  CountAvailableBedNumberVariables,
} from '~graphql/__generated__/CountAvailableBedNumber';
import { DeviceOption, TenancyOptionRateTypeOption } from '~graphql/__generated__/globalTypes';
import {
  SP_Create_Booking,
  SP_Create_BookingVariables,
} from '~graphql/__generated__/SP_Create_Booking';
import { SP_QueryNodeProperty_node_Property } from '~graphql/__generated__/SP_QueryNodeProperty';
import {
  SP_QueryNodeProperty_node_Property_buildings,
  SP_QueryNodeProperty_node_Property_buildings_floors,
} from '~graphql/__generated__/SP_QueryNodeProperty';
import { MUTATION_CREATE_BOOKING } from '~graphql/booking';
import { COUNT_AVAILABLE_BED_NUMBER } from '~graphql/filter';
import { Router, useTranslation } from '~i18n';
import { getCookie } from '~utils/cookie-operation';
import { formatToUpper, notEmpty, unique } from '~utils/js-helper';
import styles from './filter-screen.module.scss';

interface IFilterScreenProps {
  property: SP_QueryNodeProperty_node_Property;
  roomTypeId: string;
  academicYearId: string;
  tenancyOptionId: string;
  tenancyOptionRateType: TenancyOptionRateTypeOption;
  tenancyStartDate: Date | undefined;
  tenancyEndDate: Date | undefined;
  loading?: boolean;
  nominationPlanId?: string | null | undefined;
  handleCallback?: () => void;
}

const FilterScreen: React.FC<IFilterScreenProps & React.HTMLAttributes<HTMLDivElement>> = ({
  property,
  style,
  className,
  roomTypeId,
  tenancyOptionId,
  nominationPlanId,
  tenancyOptionRateType,
  academicYearId,
  tenancyStartDate,
  tenancyEndDate,
  loading,
  handleCallback,
}) => {
  const { t } = useTranslation('student_portal');
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { addToast } = useToast();

  const buildingOptions = property?.buildings?.map(building => {
    return {
      value: building?.id,
      label: building?.name,
    };
  });
  const [buildingsSelected, setBuildingsSelected] = useState(buildingOptions);
  const [maxNumOfBedsInFlat, setMaxNumOfBedsInFlat] = useState(
    Number(property?.maxNumOfBedsInFlat) > 0 ? Number(property?.maxNumOfBedsInFlat) - 1 : 0
  );

  const FilterSchema = useMemo(
    () =>
      Yup.object().shape({
        flatmates: Yup.number()
          .required()
          .min(0)
          .max(Number(property?.maxNumOfBedsInFlat) - 1),
        buildings: Yup.array(),
        floors: Yup.array(),
      }),
    [property]
  );

  const formatFloors = useCallback(
    (floors: Array<SP_QueryNodeProperty_node_Property_buildings_floors>) => {
      return floors
        .map(floor => {
          if (floor.index === 0) {
            return {
              value: floor.index,
              label: t('pre_filter.ground'),
            };
          }

          if (floor.index && floor.index < 0) {
            if (floor.index === -0.5) {
              return {
                value: floor.index,
                label: t('pre_filter.upper_lower'),
              };
            }
            const isInt = parseInt(floor?.index.toString()) === floor.index;
            if (isInt) {
              return {
                value: floor.index,
                label: `${t('select_bed.basement')} ${Math.abs(floor.index)}`,
              };
            } else {
              return {
                value: floor.index,
                label: `${t('select_bed.basement')} ${parseInt(Math.abs(floor.index).toString())}M`,
              };
            }
          }

          if (floor.index && floor.index > 0) {
            //when floor index is -0.5
            if (floor.index === 0.5) {
              return {
                value: floor.index,
                label: t('pre_filter.upper_ground'),
              };
            }
            const isInt = parseInt(floor?.index.toString()) === floor.index;
            if (isInt) {
              return {
                value: floor.index,
                label: t('pre_filter.floor', { number: floor.index }),
              };
            } else {
              return {
                value: floor.index,
                label: t('pre_filter.floor', {
                  number: `${parseInt(Math.abs(floor.index).toString())}M`,
                }),
              };
            }
          }

          return {
            value: floor.index,
            label: t('pre_filter.floor', { number: floor.index }),
          };
        })
        .sort((current, next) => {
          if (notEmpty(current.value) && notEmpty(next.value)) {
            return current.value - next.value;
          } else {
            return -1;
          }
        });
    },
    [t]
  );
  const getSelectedFloors = useCallback(
    (buildings: Array<SP_QueryNodeProperty_node_Property_buildings>) => {
      let allFloors: Array<SP_QueryNodeProperty_node_Property_buildings_floors | null> = [];
      buildings?.forEach((building: SP_QueryNodeProperty_node_Property_buildings) => {
        if (allFloors.length > 0) {
          // @ts-ignore
          allFloors = [...allFloors, ...building.floors];
        } else {
          allFloors = building.floors || [];
        }
      });
      return allFloors.filter(notEmpty);
    },
    []
  );
  const getSelectedBuildings = useCallback(
    (selectedValues: any) => {
      return selectedValues?.map((item: any) => {
        return property?.buildings?.find(buidling => buidling?.id === item.value);
      });
    },
    [property]
  );

  const getFloorsOptions = useMemo(() => {
    const selectedBuildings = getSelectedBuildings(buildingsSelected);
    return unique(formatFloors(getSelectedFloors(selectedBuildings)));
  }, [buildingsSelected, formatFloors, getSelectedBuildings, getSelectedFloors]);

  const initialValues = useMemo(
    () => ({
      buildings: buildingsSelected,
      floors: getFloorsOptions,
      flatmates: maxNumOfBedsInFlat,
    }),
    [buildingsSelected, getFloorsOptions, maxNumOfBedsInFlat]
  );

  const [floorsSelected, setFloorsSelected] = useState(getFloorsOptions);

  const getBuildingsId = useMemo(() => {
    const buildings = buildingsSelected?.map(item => item.value || null) || [];
    return buildings?.length > 0 ? buildings : [];
  }, [buildingsSelected]);

  const getFloorsIndex = useMemo(() => {
    const floorIndex = floorsSelected?.map(item => item?.value);
    if (floorIndex && floorIndex?.length > 0) {
      return floorIndex?.map(item => item.toString());
    }
    return [];
  }, [floorsSelected]);

  const { data: bedQueryResults, loading: bedNumberLoading } = useQuery<
    CountAvailableBedNumber,
    CountAvailableBedNumberVariables
  >(COUNT_AVAILABLE_BED_NUMBER, {
    ssr: false,
    variables: {
      filter: {
        roomTypeId: roomTypeId,
        tenancyOptionId: tenancyOptionId,
        tenancyStartDate: moment(tenancyStartDate).format('YYYY-MM-DD'),
        tenancyEndDate: moment(tenancyEndDate).format('YYYY-MM-DD'),
        buildingIds: getBuildingsId,
        floorIndexes: getFloorsIndex,
        maxNumOfFlatmates: maxNumOfBedsInFlat,
        ...(nominationPlanId && {
          nominationPlanId,
        }),
      },
      propertyId: property?.id || '',
    },
    fetchPolicy: 'no-cache',
  });

  const [createBooking] = useMutation<SP_Create_Booking, SP_Create_BookingVariables>(
    MUTATION_CREATE_BOOKING,
    {
      variables: {
        input: {
          propertyId: property?.id,
          tenancyOptionId: tenancyOptionId,
          academicYearId: academicYearId,
          tenancyStartDate: moment(tenancyStartDate).format('YYYY-MM-DD'),
          tenancyEndDate: moment(tenancyEndDate).format('YYYY-MM-DD'),
          roomTypeId: roomTypeId,
          tenancyOptionRateType,
          device: isMobile ? DeviceOption.MOBILE : DeviceOption.DESKTOP,
        },
      },
      onCompleted: data => {
        if (data?.studentCreateBooking?.id) {
          Router.push(`/booking-process?bookingId=${data?.studentCreateBooking?.id}`);
        }
      },
      onError: ({ graphQLErrors }) => {
        const { message } = graphQLErrors[0] || {};
        addToast(
          t('error.message', {
            errorMsg: `(${formatToUpper(message)})`,
          })
        );
      },
    }
  );

  const onChangeBuilding = (options: any, formikProps: FormikProps<any>) => {
    setBuildingsSelected(options);
    if (options?.length === 0) {
      formikProps.setValues({ ...formikProps.values, floors: '', buildings: options });
      setFloorsSelected([]);
    } else {
      const selectedBuildings = getSelectedBuildings(options);
      const selectFloors = unique(formatFloors(getSelectedFloors(selectedBuildings)));
      formikProps.setValues({ ...formikProps.values, floors: selectFloors, buildings: options });
      setFloorsSelected(selectFloors);
    }
  };

  const onSubmit = async () => {
    const filterState = {
      roomTypeId: roomTypeId,
      tenancyOptionId: tenancyOptionId,
      tenancyStartDate: moment(tenancyStartDate).format('YYYY-MM-DD'),
      tenancyEndDate: moment(tenancyEndDate).format('YYYY-MM-DD'),
      buildingIds: getBuildingsId,
      floorIndexes: getFloorsIndex,
      maxNumOfFlatmates: maxNumOfBedsInFlat,
    };
    if (handleCallback) {
      localStorage.setItem(`filter${property?.originId}`, JSON.stringify(filterState));
      await handleCallback();
    } else {
      localStorage.setItem(`filter${property?.originId}`, JSON.stringify(filterState));
      if (getCookie(Authorization)) {
        await createBooking();
      } else {
        Router.push(
          `/booking-process?propertyId=${
            property?.id
          }&initialRoomTypeId=${roomTypeId}&initialTenancyOptionId=${tenancyOptionId}&initialAcademicYearId=${academicYearId}&initialTenancyStartDate=${moment(
            tenancyStartDate
          ).format('YYYY-MM-DD')}&initialTenancyEndDate=${moment(tenancyEndDate).format(
            'YYYY-MM-DD'
          )}`
        );
      }
    }
  };

  return (
    <div className={cn(styles.root, className)} style={{ ...style }}>
      <Card title={t('pre_filter.title')} style={{ alignItems: 'baseline' }}>
        <div className={styles.wrapper}>
          <Formik initialValues={initialValues} validationSchema={FilterSchema} onSubmit={onSubmit}>
            {formikProps => {
              return (
                <Form>
                  <div className={styles.form}>
                    <SelectMulti
                      className={styles.input}
                      placeholder={t('select_buildings')}
                      options={buildingOptions}
                      menuShouldScrollIntoView={false}
                      label={t('select_bed.buildings')}
                      name="buildings"
                      isMobile={isMobile}
                      onSelect={(options: any) => {
                        onChangeBuilding(options, formikProps);
                      }}
                    />
                    <SelectMulti
                      className={styles.input}
                      placeholder={t('select_bed.floors')}
                      options={getFloorsOptions}
                      label={t('select_bed.floors')}
                      name="floors"
                      isMobile={isMobile}
                      onSelect={(options: any) => setFloorsSelected(options)}
                    />
                    <NumberInput
                      name="flatmates"
                      className={styles.input}
                      label={t('pre_filter.flatmates_maximum')}
                      placeholder={t('pre_filter.flatmates_description')}
                      min={0}
                      max={Number(property?.maxNumOfBedsInFlat) - 1}
                      onValueChange={value => {
                        setMaxNumOfBedsInFlat(value);
                      }}
                    />
                  </div>
                  <Button
                    size="large"
                    fullWidth={isMobile}
                    type="submit"
                    style={{ marginTop: 16 }}
                    loading={bedNumberLoading || formikProps.isSubmitting || loading}
                  >
                    {t('pre_filter.view_available_beds', {
                      number: bedQueryResults?.countAvailableBedNumber?.count,
                    })}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

export default FilterScreen;
